/**
 * Onboarding component shown when an app has no errors yet.
 * Provides SDK setup instructions for JavaScript and Python.
 */
import { useState } from 'react'
import { Link } from 'react-router-dom'
import { AlertCircle, Copy, Check, ExternalLink, Loader2 } from 'lucide-react'
import { Button } from '@roselabs-io/saas-shell'

interface WaitingForFirstErrorProps {
  appName: string
  apiKey?: string
  docsPath?: string
}

type Language = 'javascript' | 'python' | 'react' | 'nextjs'

export function WaitingForFirstError({
  appName,
  apiKey = 'fk_your_api_key',
  docsPath = '/docs',
}: WaitingForFirstErrorProps) {
  const [copied, setCopied] = useState<string | null>(null)
  const [selectedLang, setSelectedLang] = useState<Language>('javascript')

  const handleCopy = async (text: string, id: string) => {
    try {
      await navigator.clipboard.writeText(text)
      setCopied(id)
      setTimeout(() => setCopied(null), 2000)
    } catch {
      // Failed to copy
    }
  }

  const codeExamples: Record<Language, { install: string; usage: string; label: string }> = {
    javascript: {
      label: 'JavaScript',
      install: 'npm install @roselabs-io/falcon-js',
      usage: `import { Falcon } from '@roselabs-io/falcon-js'

const falcon = new Falcon({
  apiKey: '${apiKey}',
  appName: '${appName}',
})

// Capture errors automatically
falcon.captureException(new Error('Test error'))

// Or with try/catch
try {
  // Your code here
} catch (error) {
  falcon.captureException(error)
}`,
    },
    python: {
      label: 'Python',
      install: 'pip install roselabs-falcon',
      usage: `from roselabs_falcon import Falcon

falcon = Falcon(
    api_key="${apiKey}",
    app_name="${appName}",
)

# Capture errors automatically
try:
    # Your code here
    pass
except Exception as e:
    falcon.capture_exception(e)

# Or use the decorator for FastAPI/Flask
@falcon.capture_errors
def my_function():
    # Errors are automatically captured
    pass`,
    },
    react: {
      label: 'React',
      install: 'npm install @roselabs-io/falcon-react',
      usage: `import { FalconErrorBoundary } from '@roselabs-io/falcon-react'

// Wrap your app with the error boundary
function App() {
  return (
    <FalconErrorBoundary
      apiKey="${apiKey}"
      appName="${appName}"
      fallback={<ErrorFallback />}
    >
      <YourApp />
    </FalconErrorBoundary>
  )
}`,
    },
    nextjs: {
      label: 'Next.js',
      install: 'npm install @roselabs-io/falcon-nextjs',
      usage: `// instrumentation.ts
import { initFalcon } from '@roselabs-io/falcon-nextjs'

export function register() {
  initFalcon({
    apiKey: '${apiKey}',
    appName: '${appName}',
  })
}

// Error boundary in layout.tsx
import { FalconErrorBoundary } from '@roselabs-io/falcon-nextjs'

export default function RootLayout({ children }) {
  return (
    <FalconErrorBoundary>
      {children}
    </FalconErrorBoundary>
  )
}`,
    },
  }

  const currentExample = codeExamples[selectedLang]

  return (
    <div className="bg-gradient-to-br from-slate-50 to-slate-100 border-2 border-dashed border-slate-200 rounded-2xl p-8 text-center">
      {/* Animated waiting indicator */}
      <div className="inline-flex items-center justify-center w-16 h-16 bg-amber-100 rounded-full mb-6">
        <Loader2 className="w-8 h-8 text-amber-600 animate-spin" />
      </div>

      <h3 className="text-xl font-semibold text-slate-900 mb-2">Waiting for first error...</h3>
      <p className="text-slate-500 mb-6 max-w-md mx-auto">
        No errors have been reported for{' '}
        <span className="font-medium text-slate-700">{appName}</span> yet. Install the SDK to get
        started.
      </p>

      {/* SDK Setup */}
      <div className="bg-white rounded-xl p-6 mb-6 text-left max-w-2xl mx-auto border border-slate-200">
        {/* Language Tabs */}
        <div className="flex gap-1 mb-4 border-b border-slate-200 -mx-6 px-6">
          {(Object.keys(codeExamples) as Language[]).map(lang => (
            <button
              key={lang}
              onClick={() => setSelectedLang(lang)}
              className={`px-3 py-2 text-sm font-medium transition-colors relative ${
                selectedLang === lang ? 'text-primary-600' : 'text-slate-500 hover:text-slate-700'
              }`}
            >
              {codeExamples[lang].label}
              {selectedLang === lang && (
                <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary-600" />
              )}
            </button>
          ))}
        </div>

        {/* Step 1: Install */}
        <div className="flex items-start gap-3 mb-4">
          <div className="flex items-center justify-center w-6 h-6 bg-primary-100 rounded-full text-primary-600 text-xs font-bold flex-shrink-0">
            1
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm text-slate-700 mb-2">Install the SDK:</p>
            <div className="relative group">
              <pre className="bg-slate-100 border border-slate-200 rounded-lg p-3 overflow-x-auto text-sm font-mono text-slate-700">
                {currentExample.install}
              </pre>
              <button
                onClick={() => handleCopy(currentExample.install, 'install')}
                className="absolute top-2 right-2 p-1.5 rounded-md bg-white border border-slate-200 hover:bg-slate-50 text-slate-500 hover:text-slate-900 transition-colors opacity-0 group-hover:opacity-100"
              >
                {copied === 'install' ? (
                  <Check className="w-3.5 h-3.5 text-green-600" />
                ) : (
                  <Copy className="w-3.5 h-3.5" />
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Step 2: Configure */}
        <div className="flex items-start gap-3 mb-4">
          <div className="flex items-center justify-center w-6 h-6 bg-primary-100 rounded-full text-primary-600 text-xs font-bold flex-shrink-0">
            2
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm text-slate-700 mb-2">Initialize and capture errors:</p>
            <div className="relative group">
              <pre className="bg-slate-100 border border-slate-200 rounded-lg p-3 overflow-x-auto text-sm font-mono text-slate-700 whitespace-pre">
                {currentExample.usage}
              </pre>
              <button
                onClick={() => handleCopy(currentExample.usage, 'usage')}
                className="absolute top-2 right-2 p-1.5 rounded-md bg-white border border-slate-200 hover:bg-slate-50 text-slate-500 hover:text-slate-900 transition-colors opacity-0 group-hover:opacity-100"
              >
                {copied === 'usage' ? (
                  <Check className="w-3.5 h-3.5 text-green-600" />
                ) : (
                  <Copy className="w-3.5 h-3.5" />
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Step 3: Docs */}
        <div className="flex items-start gap-3">
          <div className="flex items-center justify-center w-6 h-6 bg-primary-100 rounded-full text-primary-600 text-xs font-bold flex-shrink-0">
            3
          </div>
          <div>
            <p className="text-sm text-slate-700">
              Trigger an error in your app, or check our{' '}
              <Link
                to={docsPath}
                className="text-primary-600 hover:underline inline-flex items-center gap-1"
              >
                documentation
                <ExternalLink className="w-3 h-3" />
              </Link>{' '}
              for more options.
            </p>
          </div>
        </div>
      </div>

      {/* Actions */}
      <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
        <Link to={docsPath}>
          <Button variant="primary" className="flex items-center gap-2">
            View Documentation
            <ExternalLink className="w-4 h-4" />
          </Button>
        </Link>
        <Button
          variant="outline"
          onClick={() => handleCopy(currentExample.usage, 'usage-btn')}
          className="flex items-center gap-2"
        >
          {copied === 'usage-btn' ? (
            <>
              <Check className="w-4 h-4 text-green-600" />
              Copied!
            </>
          ) : (
            <>
              <Copy className="w-4 h-4" />
              Copy Code
            </>
          )}
        </Button>
      </div>

      {/* Tip */}
      <div className="mt-6 flex items-start gap-2 text-left max-w-md mx-auto">
        <AlertCircle className="w-4 h-4 text-slate-400 mt-0.5 flex-shrink-0" />
        <p className="text-xs text-slate-500">
          This page will auto-refresh when your first error is received. You can also use the
          filters above to view resolved or ignored errors.
        </p>
      </div>
    </div>
  )
}
